import { create } from "zustand";
import type { ChatMessage, GameKind, MatchSnapshot, PlayerId } from "@minigames/shared";
import type { SocketStatus } from "./socket.js";
import { forgetMatch } from "./session.js";

export interface Me {
  id: PlayerId;
  username: string | null;
  firstName: string | null;
  photoUrl: string | null;
  isPremium: boolean;
}

export type Screen =
  | "boot"
  | "lobby"
  | "waiting"
  | "game"
  | "history"
  | "premium"
  | "stats"
  | "rating"
  | "error";

interface State {
  screen: Screen;
  me: Me | null;
  socketStatus: SocketStatus;
  latencyMs: number;

  /** Активная партия. */
  snapshot: MatchSnapshot | null;
  /**
   * Партия, которую игрок смотрит прямо сейчас. `null` — он в меню.
   * Снимки любой другой партии игнорируются: без этого запоздавшее
   * обновление законченной партии выкидывало игрока обратно на её экран.
   */
  activeMatchId: string | null;
  inviteLink: string | null;
  /** Соперник предложил ничью. */
  drawOfferedBy: PlayerId | null;
  chat: ChatMessage[];
  /** Последняя реакция каждого игрока — всплывает над его аватаркой. */
  reactions: Record<PlayerId, { emoji: string; at: number }>;

  error: string | null;
  /** Виден ли раздел статистики. Решает сервер, клиент только рисует. */
  isAdmin: boolean;
  /** Права по подписке. Приходят с сервера, клиент их себе не выдаёт. */
  premium: { premium: boolean; until: number | null; recurring: boolean; plan: string | null } | null;
  config: { games: GameKind[]; timeControls: Record<GameKind, string[]> } | null;

  setScreen(s: Screen): void;
  setMe(me: Me): void;
  setSocketStatus(s: SocketStatus): void;
  setLatency(ms: number): void;
  /** Явно войти в партию — только её снимки будут приниматься. */
  enterMatch(matchId: string): void;
  setSnapshot(s: MatchSnapshot): void;
  setInviteLink(link: string | null): void;
  setDrawOffer(by: PlayerId | null): void;
  addChat(message: ChatMessage): void;
  setChat(messages: ChatMessage[]): void;
  showReaction(by: PlayerId, emoji: string, at: number): void;
  clearReaction(by: PlayerId): void;
  setError(msg: string | null): void;
  setConfig(c: State["config"]): void;
  setPremium(p: State["premium"]): void;
  setIsAdmin(v: boolean): void;
  reset(): void;
}

export const useStore = create<State>((set) => ({
  screen: "boot",
  me: null,
  socketStatus: "idle",
  latencyMs: 0,
  snapshot: null,
  activeMatchId: null,
  inviteLink: null,
  drawOfferedBy: null,
  chat: [],
  reactions: {},
  error: null,
  premium: null,
  isAdmin: false,
  config: null,

  setScreen: (screen) => set({ screen }),
  setMe: (me) => set({ me }),
  setSocketStatus: (socketStatus) => set({ socketStatus }),
  setLatency: (latencyMs) => set({ latencyMs }),
  enterMatch: (activeMatchId) => set({ activeMatchId }),
  setSnapshot: (snapshot) =>
    set((s) => {
      // Снимок партии, которую мы не смотрим, игнорируем. Именно из-за
      // отсутствия этой проверки запоздавшее обновление законченной партии
      // выкидывало игрока обратно на её экран сразу после выхода в меню.
      if (s.activeMatchId !== snapshot.matchId) return {};
      return {
        snapshot,
        // предложение ничьей живёт в снимке — держим их согласованными
        drawOfferedBy:
          snapshot.drawOfferBy && snapshot.drawOfferBy !== s.me?.id ? snapshot.drawOfferBy : null,
        screen: (snapshot.status === "pending" ? "waiting" : "game") as Screen,
      };
    }),
  setInviteLink: (inviteLink) => set({ inviteLink }),
  setDrawOffer: (drawOfferedBy) => set({ drawOfferedBy }),
  addChat: (message) => set((s) => ({ chat: [...s.chat, message] })),
  setChat: (chat) => set({ chat }),
  showReaction: (by, emoji, at) =>
    set((s) => ({ reactions: { ...s.reactions, [by]: { emoji, at } } })),
  clearReaction: (by) =>
    set((s) => {
      const next = { ...s.reactions };
      delete next[by];
      return { reactions: next };
    }),
  setError: (error) => set({ error, ...(error ? { screen: "error" as const } : {}) }),
  setConfig: (config) => set({ config }),
  setPremium: (premium) => set({ premium }),
  setIsAdmin: (isAdmin) => set({ isAdmin }),
  reset: () => {
    forgetMatch();
    set({
      snapshot: null,
      activeMatchId: null,
      inviteLink: null,
      drawOfferedBy: null,
      chat: [],
      reactions: {},
      screen: "lobby",
      error: null,
    });
  },
}));
